/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";

// react-router components
import { useNavigate, useLocation } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Badge from "@mui/material/Badge";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import { apiFetch } from "utils/api";
import { unregisterPushSubscription } from "utils/push";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
} from "context";

function getPageTitle(pathname) {
  const cleaned = String(pathname || "").replace(/\/+$/, "");
  if (!cleaned || cleaned === "/") return "Dashboard";

  const map = {
    "/dashboard": "Dashboard",
    "/screens": "Bildschirme",
    "/media": "Medien",
    "/invoices": "Rechnungen",
    "/messenger": "Messenger",
    "/campaigns": "Kampagnen",
    "/profile": "Profil",
    "/control-center": "Control Center",
    "/admin": "Dashboard",
    "/admin/dashboard": "Dashboard",
    "/admin/screens": "Bildschirme",
    "/admin/media": "Medien",
    "/admin/invoices": "Rechnungen",
    "/admin/messenger": "Messenger",
    "/admin/profile": "Profil",
    "/admin/control-center": "Control Center",
  };

  if (map[cleaned]) return map[cleaned];

  const parts = cleaned.split("/").filter(Boolean);
  const last = parts[parts.length - 1] || "";
  return last
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function DashboardNavbar({ absolute, light, isMini }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, darkMode } = controller;
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [unreadChats, setUnreadChats] = useState(0);
  const messengerRoute = location.pathname.startsWith("/admin") ? "/admin/messenger" : "/messenger";
  const pageTitle = getPageTitle(location.pathname);

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  useEffect(() => {
    let stopped = false;

    const load = async () => {
      try {
        const [notificationRes, chatRes] = await Promise.all([
          apiFetch("/api/notifications?limit=8"),
          apiFetch("/api/messages/unread-count"),
        ]);
        if (stopped) return;
        setNotifications(Array.isArray(notificationRes?.items) ? notificationRes.items : []);
        setUnreadNotifications(Number(notificationRes?.unreadCount || 0));
        setUnreadChats(Number(chatRes?.unreadCount || 0));
      } catch {
        if (!stopped) {
          setNotifications([]);
          setUnreadNotifications(0);
          setUnreadChats(0);
        }
      }
    };

    void load();
    const id = setInterval(() => {
      void load();
    }, 15000);

    const handleRefresh = () => {
      void load();
    };
    window.addEventListener("solvit-chat-read", handleRefresh);
    window.addEventListener("solvit-notifications-refresh", handleRefresh);

    return () => {
      stopped = true;
      clearInterval(id);
      window.removeEventListener("solvit-chat-read", handleRefresh);
      window.removeEventListener("solvit-notifications-refresh", handleRefresh);
    };
  }, []);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleLogout = async () => {
    await unregisterPushSubscription().catch(() => null);
    localStorage.removeItem("solvit_auth_token");
    localStorage.removeItem("solvit_profile");
    navigate("/authentication/sign-in", { replace: true });
  };

  const handleOpenNotifications = (event) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleCloseNotifications = () => {
    setNotificationAnchor(null);
  };

  const handleMarkNotificationRead = async (id) => {
    try {
      await apiFetch(`/api/notifications/${id}/read`, { method: "PATCH" });
      setNotifications((prev) => prev.map((item) => (item.id === id ? { ...item, readAt: new Date().toISOString() } : item)));
      setUnreadNotifications((prev) => Math.max(0, prev - 1));
    } catch {
      // ignore in navbar action
    }
  };

  // Styles for the navbar icons
  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    },
  });

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <MDTypography
            variant="h4"
            fontWeight="bold"
            color={light ? "white" : "dark"}
            sx={{ lineHeight: 1, letterSpacing: "0.01em" }}
          >
            {pageTitle}
          </MDTypography>
        </MDBox>
        {isMini ? null : (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
            <MDBox color={light ? "white" : "inherit"}>
              <IconButton
                sx={{ ...navbarIconButton, ml: 1.35 }}
                size="small"
                disableRipple
                color="inherit"
                onClick={handleOpenNotifications}
              >
                <Badge color="error" variant="dot" invisible={unreadNotifications <= 0}>
                  <Icon sx={iconsStyle} fontSize="small">
                    notifications_none
                  </Icon>
                </Badge>
              </IconButton>
              <Menu
                anchorEl={notificationAnchor}
                open={Boolean(notificationAnchor)}
                onClose={handleCloseNotifications}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{ sx: { width: 360, maxWidth: "92vw" } }}
              >
                <MDBox px={2} py={1.25} display="flex" alignItems="center" justifyContent="space-between">
                  <MDTypography variant="button" fontWeight="medium">
                    Benachrichtigungen
                  </MDTypography>
                  <MDTypography variant="caption" color="text">
                    {unreadNotifications} ungelesen
                  </MDTypography>
                </MDBox>
                <Divider />
                {notifications.length === 0 ? (
                  <MDBox px={2} py={1.5}>
                    <MDTypography variant="caption" color="text">
                      Keine neuen Benachrichtigungen.
                    </MDTypography>
                  </MDBox>
                ) : (
                  notifications.map((item) => (
                    <MenuItem
                      key={item.id}
                      sx={{ alignItems: "flex-start", gap: 1.1, whiteSpace: "normal", py: 1.05 }}
                    >
                      <MDBox flex={1}>
                        <MDTypography variant="caption" fontWeight={item.readAt ? "regular" : "medium"}>
                          {item.message}
                        </MDTypography>
                        <MDTypography variant="caption" color="text" display="block" mt={0.3}>
                          {item?.customer?.companyName || ""} {item?.createdAt ? `• ${new Date(item.createdAt).toLocaleString("de-DE")}` : ""}
                        </MDTypography>
                      </MDBox>
                      {!item.readAt && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleMarkNotificationRead(item.id);
                          }}
                        >
                          <Icon fontSize="small">done</Icon>
                        </IconButton>
                      )}
                    </MenuItem>
                  ))
                )}
              </Menu>
              <IconButton
                sx={{ ...navbarIconButton, ml: 1.35 }}
                size="small"
                disableRipple
                color="inherit"
                onClick={() => navigate(messengerRoute)}
              >
                <Badge color="error" variant="dot" invisible={unreadChats <= 0}>
                  <Icon sx={iconsStyle} fontSize="small">
                    send
                  </Icon>
                </Badge>
              </IconButton>
              <MDButton
                variant="contained"
                color="primary"
                onClick={handleLogout}
                sx={{ ml: 1.2, minWidth: 120, py: 0.85 }}
              >
                Abmelden
              </MDButton>

              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon sx={iconsStyle} fontSize="medium">
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
